import * as React from 'react';
import {ReactElement} from 'react';
import {
    AcceptIcon,
    Button,
    ChevronEndIcon,
    ChevronStartIcon,
    CloseIcon,
    DropdownProps,
    Flex,
    FlexItem,
    Loader,
    Menu,
    Text,
    UndoIcon
} from '@fluentui/react-northstar'
import './newMessage.scss';
import './teamTheme.scss';
import {createDraftNotification, getDraftNotification, updateDraftNotification} from '../../apis/messageListApi';
import CreateMessage from "./Steps/CreateMessage";
import Configuration from "./Steps/Configuration";
import ConfirmDialog from "../Dialogs/ConfirmDialog";
import update from 'immutability-helper';
import {getInitAdaptiveCard, updateCard,} from '../AdaptiveCard/adaptiveCard';
import {ImageGallery} from "../../Interfaces/ImageGallery";
import {blobsRootFolderName, retrieveBlobImages} from "../../apis/blobImageApi";
import {IDraftMessage, IMessageChannel, IMessageState, ISendType} from "../../Interfaces/Message";
import {ITranslationMessage} from "../../Interfaces/TranslationMessage";
import {ButtonActionType, IButtonMessage} from "../../Interfaces/ButtonMessage";
import languageObject from "../../translations";
import {AppContext, IAppContextState} from "../../services/AppContext";
import ChooseAudience from "./Steps/ChooseAudience";
import GroupApi from "../../apis/groupApi";
import {IGroup} from "../../Interfaces/IGroup";
import {GuidModule} from "../../modules/Guid.module";
import {UUID} from 'adaptivecards';
import {CopilotApi} from "../../apis/copilotApi";
import withRouter, {WithRouterProps} from "../../services/TelemetryService/withRouter.hook";
import {IFilePickerFile} from "../FilePickerButton/FilePickerButton.interfaces";

enum Pages {
    Configuration = "Configuration",
    CreateMessage = "CreateMessage",
    ChooseAudience = "ChooseAudience",
    Translation = "Translation"
}

export type dropdownItem = {
    key: string,
    header: string,
    content: string,
    team: {
        id: string,
    },
}

export interface INewMessageState {
    page: Pages,
    exists?: boolean,
    loading: boolean,
    noResultMessage: string,
    draftMessage: IDraftMessage,
    currentTranslation: number,
    availableGroups: Array<IGroup>,
    previousData: {
        draftMessage: IDraftMessage,
    },
    showCancelDialog: boolean,
    galleryImages: Array<ImageGallery>,
    updatingDatabase: boolean,
    selectedRadioBtn: string,
    groupsOptionSelected: boolean,
    groupAccess: boolean,
    selectedGroups: dropdownItem[],
    searchingGroups: boolean,
    selectedMailingList: string,
}

export interface INewMessageProps extends WithRouterProps {
    getDraftMessagesList?: any;

}

export const defaultDraftMessage: IDraftMessage = {
    defaultLanguage: {
        message: "",
        image: undefined,
        isDefault: true,
        askFeedback: false,
        author: "",
        buttons: new Array<IButtonMessage>(),
        id: "",
        title: "",
        imageLink: undefined,
        bannerLink: undefined,
    },
    tenantId: "",
    id: "",
    state: IMessageState.Draft,
    topicsId: new Array<string>(),
    translations: new Array<ITranslationMessage>(),
    groupId: "",
    sendType: ISendType.None,
    channel: IMessageChannel.Channel_Teams,
    gptId: undefined,
    authorName: "",
}

let searchGroupTimeout: NodeJS.Timeout | undefined = undefined;

/**
 * New Message
 */
class NewMessage extends React.Component<INewMessageProps, INewMessageState> {
    // Define component context
    static contextType = AppContext;
    context!: IAppContextState;

    private readonly card: any;

    constructor(props: INewMessageProps) {
        super(props);
        this.card = getInitAdaptiveCard();

        this.state = {
            page: Pages.Configuration,
            loading: true,
            noResultMessage: "",
            currentTranslation: -1,
            draftMessage: {...defaultDraftMessage},
            previousData: {} as any,
            showCancelDialog: false,
            galleryImages: new Array<ImageGallery>(),
            updatingDatabase: false,
            groupAccess: false,
            selectedRadioBtn: "allUsers",
            searchingGroups: false,
            selectedGroups: new Array<dropdownItem>(),
            groupsOptionSelected: false,
            availableGroups: new Array<IGroup>(),
            selectedMailingList: "",
        }

        this.escFunction = this.escFunction.bind(this);
    }

    /**
     * Component initialization
     */
    async componentDidMount() {
        await this.fetchAvailableImages();
        await this.fetchMessage();
        document.addEventListener("keydown", this.escFunction, false);
        this.updateCard();
        this.setState(prevState => {
            let draftMessage = prevState.draftMessage;
            if (!prevState.exists && this.context.tenant.enableGpt) draftMessage.channel = IMessageChannel.Channel_GPTPro;
            return {...prevState, draftMessage, loading: false};
        });
    }

    /**
     * Component destruction
     */
    public componentWillUnmount() {
        document.removeEventListener("keydown", this.escFunction, false);
    }

    /**
     * Retrieve available images
     */
    fetchAvailableImages = async () => {
        return new Promise<void>((resolve) => {
            retrieveBlobImages().then(images => {
                this.setState({galleryImages: images}, resolve);
            })
        })
    }

    fetchMessage = async (): Promise<void> => {
        return new Promise<void>(async resolve => {
            const path = window.location.pathname.replace("newmessage", "");
            const messageId = path.slice(path.lastIndexOf("/") + 1);
            if (!GuidModule.isValidGuid(messageId)) {
                const french = this.context.languages.list.find(l => l.code === "FR");
                if (!french) throw new Error("Can't find french language");
                const draftMessage = update(this.state.draftMessage, {defaultLanguage: {id: {$set: french.id}}});
                this.setState({draftMessage, previousData: {draftMessage}});
                return resolve();
            }
            const message = await getDraftNotification(messageId);
            const isAudienceGroup = message.sendType === ISendType.Group;
            const isAudienceMailingList = message.sendType === ISendType.MailingList;
            const selectedGroups = new Array<dropdownItem>();
            if (isAudienceGroup) {
                const group: IGroup | undefined = await GroupApi.getGroup(message.groupId);
                selectedGroups.push({
                    key: group.id,
                    header: group.displayName,
                    content: group.mail,
                    team: {id: group.id}
                })
            }
            [message.defaultLanguage, ...message.translations].forEach(translation => {
                translation.image = this.state.galleryImages.find(i => i.imageLink === blobsRootFolderName + translation.imageLink);
                translation.banner = this.state.galleryImages.find(i => i.imageLink === blobsRootFolderName + translation.bannerLink);
            });
            let selectedRadioBtn = "allUsers";
            if (isAudienceGroup) selectedRadioBtn = "groups";
            else if (isAudienceMailingList) selectedRadioBtn = "mailing-lists";
            this.setState({
                exists: true,
                draftMessage: message,
                previousData: {draftMessage: message},
                groupsOptionSelected: isAudienceGroup,
                selectedRadioBtn,
                selectedGroups,
                selectedMailingList: message.mailingListId ?? "",
            }, resolve)
        })
    }

    /**
     * Update available images
     * @param images images
     */
    handleImagesUpdate = (images: Array<ImageGallery>): Promise<void> => {
        return new Promise<void>(resolve => {
            this.setState({galleryImages: images}, resolve);
        });
    }

    private hasMessageChanged = () => {
        const previousData = JSON.stringify(this.state.previousData.draftMessage);
        const currentData = JSON.stringify(this.state.draftMessage);
        return previousData !== currentData;
    }

    private isPageDisabled = (page: Pages, translationMessage?: ITranslationMessage) => {
        switch (page) {
            case Pages.Configuration:
                return false;
            case Pages.CreateMessage:
                return !this.isPageValid(Pages.Configuration);
            case Pages.ChooseAudience:
                for (let i = 0; i < this.state.draftMessage.translations.length; i++)
                    if (!this.isTranslationMessageValid(this.state.draftMessage.translations[i]))
                        return true;
                return !this.isPageValid(Pages.Configuration) || !this.isPageValid(Pages.CreateMessage);
            case Pages.Translation:
                if (translationMessage) {
                    if (translationMessage === this.state.draftMessage.translations[0]) {
                        return !this.isPageValid(Pages.Configuration) || !this.isPageValid(Pages.CreateMessage)
                    } else {
                        for (let i = this.state.draftMessage.translations.indexOf(translationMessage) - 1; i >= 0; i--)
                            if (!this.isTranslationMessageValid(this.state.draftMessage.translations[i]))
                                return true;
                        return !this.isPageValid(Pages.Configuration) || !this.isPageValid(Pages.CreateMessage);
                    }
                }
                break;
        }
    }

    private getMenuItems = () => {
        const menuItems = new Array<object>();
        [Pages.Configuration, Pages.CreateMessage, Pages.Translation, Pages.ChooseAudience].forEach(page => {
            if (page === Pages.Translation) {
                this.state.draftMessage.translations.forEach((translation, index) => {
                    const languageName = this.context.languages.list.find(l => l.id === translation.id)?.languageName ?? "";
                    menuItems.push({
                        key: "translation_" + languageName,
                        disabled: this.isPageDisabled(page, translation),
                        content: (
                            <Flex wrap styles={{width: "170px"}}>
                                <Text content={languageObject.translate("AddLanguageTranslation", {
                                    Language: languageObject.translate(languageName).toLowerCase()
                                })}/>
                            </Flex>
                        ),
                        icon: this.isPageDisabled(page, translation) ? null : this.isPageValid(page, translation) ?
                            <AcceptIcon styles={{color: "green"}}/> : <CloseIcon styles={{color: "red"}}/>,
                        onClick: () => {
                            this.setState({page, currentTranslation: index}, () => this.updateCard());
                        },
                        wrapper: {
                            className: (this.state.page === page && this.state.currentTranslation === index) ?
                                "active_menu_item" : ""
                        }
                    });
                })
            } else {
                menuItems.push({
                    key: page,
                    disabled: this.isPageDisabled(page),
                    content: (
                        <Flex wrap styles={{width: "170px"}}>
                            <Text content={languageObject.translate(page)}/>
                        </Flex>
                    ),
                    icon: this.isPageDisabled(page) ? null : this.isPageValid(page) ?
                        <AcceptIcon styles={{color: "green"}}/> : <CloseIcon styles={{color: "red"}}/>,
                    onClick: () => {
                        this.setState({page, currentTranslation: -1}, () => this.updateCard());
                    },
                    wrapper: {
                        className: this.state.page === page ? "active_menu_item" : ""
                    }
                });
            }
        });
        return menuItems;
    }

    private onRadioGroupSelected = (_: any, data: any) => {
        const value = data.value;
        const isGroupsSelected = data.value === 'groups';
        const isMailingListSelected = data.value === 'mailing-lists';
        this.setState({
            selectedRadioBtn: value,
            groupsOptionSelected: isGroupsSelected,
            selectedGroups: isGroupsSelected ? this.state.selectedGroups : [],
            selectedMailingList: isMailingListSelected ? this.state.selectedMailingList : "",
        });
    }

    private onGroupsChange = (_: React.SyntheticEvent, data: DropdownProps | null) => {
        const value: dropdownItem = data?.value as dropdownItem;
        const selectedGroups = !!value ? [value] : [];
        this.setState({selectedGroups});
    }

    private onGroupSearch = (itemList: any, searchQuery: string) => {
        return itemList.filter(
            (item: {
                header: string;
                content: string;
            }) => (item.header && item.header.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1) ||
                (item.content && item.content.toLowerCase().indexOf(searchQuery.toLowerCase()) !== -1),
        );
    }

    private onGroupSearchQueryChange = async (_: React.SyntheticEvent, itemsData: DropdownProps) => {
        if (!!searchGroupTimeout) clearTimeout(searchGroupTimeout);
        if (!itemsData.searchQuery) {
            this.setState({noResultMessage: ""});
        } else if (itemsData.searchQuery && itemsData.searchQuery.length <= 2) {
            this.setState({
                searchingGroups: false,
                noResultMessage: languageObject.translate("NoMatchMessage"),
            });
        } else if (itemsData.searchQuery && itemsData.searchQuery.length > 2) {
            const items = itemsData.items as dropdownItem[];
            const result = items && items.find(
                (item: { header: string; }) => item.header.toLowerCase() === (itemsData.searchQuery ?? "").toLowerCase()
            )
            if (result) return;
            this.setState({searchingGroups: true, noResultMessage: ""});
            searchGroupTimeout = setTimeout(async () => {
                searchGroupTimeout = undefined;
                const groups = await GroupApi.searchForGroups(itemsData.searchQuery ?? "");
                try {
                    this.setState({
                        searchingGroups: false,
                        noResultMessage: languageObject.translate("NoMatchMessage"),
                        availableGroups: groups
                    });
                } catch (error) {
                    return error;
                }
            }, 800);
        }
    }

    private getGroupItems = () => {
        const dropdownItems: dropdownItem[] = this.state.availableGroups.map((group): dropdownItem => ({
            key: group.id,
            header: group.displayName,
            content: group.mail,
            team: {
                id: group.id
            }
        }))
        return dropdownItems;
    }

    private onMailingListChange = (_: React.SyntheticEvent, data: DropdownProps | null) => {
        const mailingListId = (data?.value as { key: string })?.key;
        this.setState({selectedMailingList: mailingListId});
    }

    public render(): ReactElement {
        if (this.state.loading) {
            return (
                <Flex className={"full_layout"}>
                    <Loader styles={{width: "100%"}}/>
                </Flex>
            );
        } else {
            let mainContent;
            switch (this.state.page) {
                case Pages.Configuration:
                    mainContent = (
                        <Configuration
                            draftMessage={this.state.draftMessage}
                            onDefaultLanguageChange={this.onDefaultLanguageChange}
                            onAddOrRemoveTranslation={this.onAddOrRemoveTranslation}
                            onTopicsChange={this.onTopicsChange}
                            onCanalChange={this.onCanalChange}
                            onGptChange={this.onGptChange}
                        />
                    )
                    break;
                case Pages.CreateMessage:
                case Pages.Translation:
                    const data = this.state.currentTranslation === -1 ? this.state.draftMessage.defaultLanguage :
                        this.state.draftMessage.translations[this.state.currentTranslation];
                    const languageName = this.context.languages.list.find(l => l.id === data.id)?.languageName ?? "";
                    const header = this.state.currentTranslation === -1 ? languageObject.translate("WriteMessage") :
                        languageObject.translate("WriteMessageIn", {
                            Language: languageObject.translate(languageName).toLowerCase()
                        });
                    mainContent = (
                        <CreateMessage
                            data={data}
                            header={header}
                            onTitleChanged={this.onTitleChanged}
                            onSummaryChanged={this.onSummaryChanged}
                            onSubMessageChanged={this.onSubMessageChanged}
                            onAuthorChanged={this.onAuthorChanged}
                            onBtnTitleChanged={this.onBtnTitleChanged}
                            onBtnLinkChanged={this.onBtnLinkChanged}
                            onBtnActionTypeChanged={this.onBtnActionTypeChanged}
                            onBtnScriptChanged={this.onBtnScriptChanged}
                            onAskFeedbackChange={this.onAskFeedbackChange}
                            onAddButton={this.onAddButton}
                            onRemoveButton={this.onRemoveButton}
                            canAddButtons={this.canAddButtons}
                            galleryImages={this.state.galleryImages}
                            onImageSelected={this.onImageChanged}
                            onBannerSelected={this.onBannerChanged}
                            onRefreshGalleryImages={this.fetchAvailableImages}
                            onImagesUpdated={this.handleImagesUpdate}
                            enableCopilot={this.context.tenant.enableCopilot}
                            channel={this.state.draftMessage.channel}
                            gptName={this.context.gpts.list.find(g => g.id === this.state.draftMessage.gptId)?.fullName ?? undefined}
                        />
                    )
                    break;
                case Pages.ChooseAudience:
                    mainContent = (
                        <ChooseAudience
                            draftMessage={this.state.draftMessage}
                            selectedRadioBtn={this.state.selectedRadioBtn}
                            onRadioGroupSelected={this.onRadioGroupSelected}
                            groupsOptionSelected={this.state.groupsOptionSelected}
                            groupAccess={this.state.groupAccess}
                            onGroupSearch={this.onGroupSearch}
                            getGroupItems={this.getGroupItems}
                            selectedGroups={this.state.selectedGroups}
                            onGroupSearchQueryChange={this.onGroupSearchQueryChange}
                            onGroupsChange={this.onGroupsChange}
                            noResultMessage={this.state.noResultMessage}
                            searchingGroups={this.state.searchingGroups}
                            mailingLists={this.context.usersLists.lists}
                            selectedMailingList={this.state.selectedMailingList}
                            onMailingListChange={this.onMailingListChange}
                        />
                    );
                    break;
                default:
                    mainContent = <div>Error</div>;
                    break;
            }

            const isInTeams = this.context.teams.isTeamsIframe;
            const paddingTop = isInTeams ? "0" : "20px";

            return (
                <Flex fill column styles={{paddingTop}} className="full_layout_new_messages">
                    {isInTeams ? null : <Flex className={"top-divider"}/>}
                    <Flex fill>
                        <Menu activeIndex={this.state.page} className={"menu"} items={this.getMenuItems()} vertical/>
                        <Flex fill column className={"pages"}>
                            <Flex fill className={"pos-relative"}>
                                <Flex className={"fill-absolute main_content"}>
                                    {mainContent}
                                </Flex>
                            </Flex>
                            <Flex gap={"gap.small"} className={"footer_actions"}>
                                <Button icon={<CloseIcon outline/>} content={languageObject.translate("Cancel")}
                                        onClick={() => {
                                            if (this.hasMessageChanged()) this.setState({showCancelDialog: true});
                                            else this.props.navigate("/messages")
                                        }}/>
                                <FlexItem push>
                                    <Flex gap={"gap.small"}>
                                        {this.state.page !== Pages.Configuration &&
                                            <Button icon={<ChevronStartIcon outline/>} iconPosition={"before"}
                                                    content={languageObject.translate("Back")} onClick={this.onBack}/>
                                        }
                                        <Button id="saveBtn" primary icon={<ChevronEndIcon outline/>}
                                                iconPosition={"after"}
                                                content={languageObject.translate(this.state.page === Pages.ChooseAudience ? "SaveAsDraft" : "Next")}
                                                disabled={this.isNextBtnDisabled()}
                                                onClick={this.state.page === Pages.ChooseAudience ? this.onSave : this.onNext}/>
                                    </Flex>
                                </FlexItem>
                            </Flex>
                        </Flex>
                        <ConfirmDialog
                            show={this.state.showCancelDialog}
                            title={languageObject.translate("UnsavedChanges")}
                            content={languageObject.translate("DiscardEdits")}
                            onConfirm={() => this.props.navigate("/messages")}
                            onCancel={() => this.setState({showCancelDialog: false})}
                            button={{
                                title: languageObject.translate("Discard"),
                                icon: <UndoIcon outline/>
                            }}
                        />
                    </Flex>
                </Flex>
            );
        }
    }

    private isNextBtnDisabled = () => {
        return !this.isPageValid(this.state.page, this.state.draftMessage.translations[this.state.currentTranslation]);
    }

    private isTranslationMessageValid = (translation: ITranslationMessage) => {
        for (let i = 0; i < translation.buttons.length; i++) {
            const btnTitle = translation.buttons[i].title;
            const btnLink = translation.buttons[i].link;
            if (btnTitle === "" || !btnLink?.startsWith("https://")) return false;
        }
        return translation.title !== "";
    }

    private isPageValid = (page: Pages, translation?: ITranslationMessage): boolean => {
        switch (page) {
            case Pages.Configuration:
                return this.state.draftMessage.topicsId.length > 0;
            case Pages.CreateMessage:
                for (let i = 0; i < this.state.draftMessage.defaultLanguage.buttons.length; i++) {
                    let isActionValid = false;
                    let actionType = this.state.draftMessage.defaultLanguage.buttons[i].actionType;
                    let button = this.state.draftMessage.defaultLanguage.buttons[i];
                    if (actionType === ButtonActionType.Link) {
                        isActionValid = !!button.link && button.link.startsWith("https://");
                    } else if (actionType === ButtonActionType.Script) {
                        isActionValid = !!button.script;
                    }

                    if (button.title === "" || !isActionValid) return false;
                }
                return this.state.draftMessage.defaultLanguage.title !== "";
            case Pages.ChooseAudience:
                const isAllUsersSelected = this.state.selectedRadioBtn === "allUsers";
                const isGroupSelected = this.state.selectedRadioBtn === "groups";
                const isGroupValid = this.state.selectedGroups.length > 0;
                const isMailingListSelected = this.state.selectedRadioBtn === "mailing-lists";
                const isMailingListValid = !!this.state.selectedMailingList;
                return isAllUsersSelected || (isGroupSelected && isGroupValid) || (isMailingListSelected && isMailingListValid);
            case Pages.Translation:
                return translation ? this.isTranslationMessageValid(translation) : false;
            default:
                return false;
        }
    }

    private onDefaultLanguageChange = (languageId: string) => {
        let defaultLanguageTranslationIndex = -1;
        for (let i = 0; i < this.state.draftMessage.translations.length; i++) {
            if (this.state.draftMessage.translations[i].id === languageId) {
                defaultLanguageTranslationIndex = i;
                break;
            }
        }
        this.setState({
            draftMessage: update(this.state.draftMessage, {
                defaultLanguage: {id: {$set: languageId}},
                translations: defaultLanguageTranslationIndex > -1 ?
                    {$splice: [[defaultLanguageTranslationIndex, 1]]} : {}
            })
        })
    }

    private onTranslationMessageChange = (key: string, value: any, index?: number) => new Promise<void>(resolve => {
        if (this.state.currentTranslation === -1) {
            this.setState({
                draftMessage: update(this.state.draftMessage, {
                    defaultLanguage: index !== undefined ?
                        {buttons: {[index]: {[key]: {$set: value}}}} : {[key]: {$set: value}}
                })
            }, () => {
                this.updateCard();
                resolve();
            });
        } else {
            this.setState({
                draftMessage: update(this.state.draftMessage, {
                    translations: {
                        [this.state.currentTranslation]: index !== undefined ?
                            {buttons: {[index]: {[key]: {$set: value}}}} : {[key]: {$set: value}}
                    }
                })
            }, () => {
                this.updateCard();
                resolve();
            });
        }
    });

    private onSave = () => {
        this.setState({loading: true});

        const imagesSuggestionsToUpload = new Array<string>();
        if (this.state.draftMessage.defaultLanguage.image?.fromSuggestions)
            imagesSuggestionsToUpload.push(this.state.draftMessage.defaultLanguage.image.content);
        imagesSuggestionsToUpload.push(...this.state.draftMessage.translations
            .filter(l => !!l.image?.content && l.image?.fromSuggestions).map(l => l.image?.content ?? ""))

        const defaultLanguage: ITranslationMessage = this.state.draftMessage.defaultLanguage;
        if (!!this.state.draftMessage.defaultLanguage.image) {
            defaultLanguage.imageLink = this.state.draftMessage.defaultLanguage.image.imageLink.replace(blobsRootFolderName, "");
            delete defaultLanguage.image;
        }
        if (!!this.state.draftMessage.defaultLanguage.banner) {
            defaultLanguage.bannerLink = this.state.draftMessage.defaultLanguage.banner.imageLink.replace(blobsRootFolderName, "");
            delete defaultLanguage.banner;
        }

        const additionalLanguages = this.state.draftMessage.translations.map(al => {
            const language: ITranslationMessage = al;
            if (!!language.image) {
                language.imageLink = language.image.imageLink.replace(blobsRootFolderName, "");
                delete language.image;
            }
            if (!!language.banner) {
                language.bannerLink = language.banner.imageLink.replace(blobsRootFolderName, "");
                delete language.banner;
            }
            return language;
        });

        let sendType = ISendType.None;
        if (this.state.selectedRadioBtn === "allUsers") sendType = ISendType.EveryOne;
        else if (this.state.selectedRadioBtn === "groups") sendType = ISendType.Group;
        else if (this.state.selectedRadioBtn === "mailing-lists") sendType = ISendType.MailingList;

        const data: IDraftMessage = {
            ...this.state.draftMessage,
            defaultLanguage,
            translations: additionalLanguages,
            tenantId: this.context.teams.tenantId as string,
            groupId: !!this.state.selectedGroups[0] ? this.state.selectedGroups[0].team.id : "",
            sendType,
            mailingListId: this.state.selectedMailingList,
        }

        try {
            imagesSuggestionsToUpload.forEach(async url => await CopilotApi.uploadImageSuggestion({url}));
            if (this.state.exists) updateDraftNotification(data).then(() => this.props.navigate("/messages"));
            else createDraftNotification(data).then(() => this.props.navigate("/messages"));
        } catch (error) {
            return error;
        }
    }

    public escFunction(event: any) {
        if (event.keyCode === 27 || (event.key === "Escape")) {
            if (this.hasMessageChanged()) this.setState({showCancelDialog: true});
            else this.props.navigate("/messages")
        }
    }

    private onNext = () => {
        let nextPage: Pages;
        switch (this.state.page) {
            case Pages.Configuration:
                nextPage = Pages.CreateMessage;
                break;
            case Pages.CreateMessage:
                if (this.state.draftMessage.translations.length > 0)
                    nextPage = Pages.Translation
                else
                    nextPage = Pages.ChooseAudience;
                break;
            case Pages.Translation:
                nextPage = this.state.currentTranslation === this.state.draftMessage.translations.length - 1 ?
                    Pages.ChooseAudience : Pages.Translation;
                break;
            default:
                return;
        }
        this.setState({
            page: nextPage, currentTranslation: nextPage === Pages.Translation ?
                this.state.currentTranslation + 1 : -1,
        }, () => this.updateCard());
    }

    private onBack = () => {
        const stateUpdate = {} as any;
        let previousPage;
        switch (this.state.page) {
            case Pages.CreateMessage:
                previousPage = Pages.Configuration;
                break;
            case Pages.Translation:
                previousPage = this.state.currentTranslation === 0 ? Pages.CreateMessage : Pages.Translation;
                stateUpdate.currentTranslation = this.state.currentTranslation - 1;
                break;
            case Pages.ChooseAudience:
                previousPage = this.state.draftMessage.translations.length > 0 ? Pages.Translation : Pages.CreateMessage;
                stateUpdate.currentTranslation = this.state.draftMessage.translations.length - 1;
                break;
            default:
                return;
        }
        this.setState({page: previousPage, ...stateUpdate}, () => {
            this.updateCard();
        });
    }

    private onTopicsChange = (value: Array<string>) => {
        this.setState({
            draftMessage: update(this.state.draftMessage, {topicsId: {$set: value}})
        });
    }

    private onCanalChange = (value: IMessageChannel) => {
        this.setState({
            draftMessage: update(this.state.draftMessage, {channel: {$set: value}, gptId: {$set: ''}})
        });
    }

    private onGptChange = (value: string) => {
        this.setState({
            draftMessage: update(this.state.draftMessage, {gptId: {$set: value}})
        });
    }

    private onAddOrRemoveTranslation = (languagesIds: Array<string>) => {
        const translations = this.state.draftMessage.translations
            .filter(translation => languagesIds.includes(translation.id));
        if (translations.length !== languagesIds.length) {
            const existingLanguages = this.state.draftMessage.translations.map(t => t.id);
            languagesIds.forEach(id => {
                if (!existingLanguages.includes(id))
                    translations.push({
                        id: id,
                        title: "",
                        imageLink: undefined,
                        message: "",
                        author: "",
                        buttons: new Array<IButtonMessage>(),
                        askFeedback: false,
                    } as ITranslationMessage);
            })
        }
        this.setState({
            draftMessage: update(this.state.draftMessage, {translations: {$set: translations}})
        });
    }

    /**
     * When feedback card option changes
     * @param value
     */
    private onAskFeedbackChange = (value: boolean) => {
        if (this.state.currentTranslation === -1) {
            const buttonsLength = this.state.draftMessage.defaultLanguage.buttons.length;
            this.setState({
                draftMessage: update(this.state.draftMessage, {
                    defaultLanguage: {
                        askFeedback: {$set: value},
                        buttons: (buttonsLength === 5 && value) ? {
                            $splice: [[4, 1]]
                        } : {}
                    }
                })
            }, () => this.updateCard());
        } else {
            this.setState({
                draftMessage: update(this.state.draftMessage, {
                    translations: {
                        [this.state.currentTranslation]: {askFeedback: {$set: value}}
                    }
                })
            }, () => this.updateCard());
        }
    }

    private canAddButtons = () => {
        if (this.state.currentTranslation === -1) {
            const hasFeedback = this.state.draftMessage.defaultLanguage.askFeedback;
            return this.state.draftMessage.defaultLanguage.buttons.length < (hasFeedback ? 4 : 5);
        } else {
            const hasFeedback = this.state.draftMessage.translations[this.state.currentTranslation].askFeedback;
            return this.state.draftMessage.translations[this.state.currentTranslation].buttons.length < (hasFeedback ? 4 : 5)
        }
    }

    private onAddButton = () => {
        if (this.state.currentTranslation === -1) {
            const hasFeedback = this.state.draftMessage.defaultLanguage.askFeedback;
            if (this.state.draftMessage.defaultLanguage.buttons.length < (hasFeedback ? 4 : 5)) {
                let nId = this.state.draftMessage.defaultLanguage.id ?? undefined;
                this.setState({
                    draftMessage: update(this.state.draftMessage, {
                        defaultLanguage: {
                            buttons: {
                                $push: [{
                                    id: UUID.generate(),
                                    translationId: nId,
                                    actionType: ButtonActionType.Link
                                } as IButtonMessage]
                            }
                        }
                    })
                });
            }
        } else {
            const hasFeedback = this.state.draftMessage.translations[this.state.currentTranslation].askFeedback;
            if (this.state.draftMessage.translations[this.state.currentTranslation].buttons.length < (hasFeedback ? 4 : 5)) {
                let nId = this.state.draftMessage.translations[this.state.currentTranslation].id ?? undefined;
                this.setState({
                    draftMessage: update(this.state.draftMessage, {
                        translations: {
                            [this.state.currentTranslation]: {
                                buttons: {
                                    $push: [{
                                        id: UUID.generate(),
                                        translationId: nId,
                                        actionType: ButtonActionType.Link
                                    } as IButtonMessage]
                                }
                            }
                        }
                    })
                });
            }
        }
    }

    private onRemoveButton = (index: number) => {
        if (this.state.currentTranslation === -1) {
            this.setState({
                draftMessage: update(this.state.draftMessage, {defaultLanguage: {buttons: {$splice: [[index, 1]]}}})
            }, () => this.updateCard());
        } else {
            this.setState({
                draftMessage: update(this.state.draftMessage, {
                    translations: {
                        [this.state.currentTranslation]: {buttons: {$splice: [[index, 1]]}}
                    }
                })
            }, () => this.updateCard());
        }
    }

    private onTitleChanged = async (value: string) => {
        await this.onTranslationMessageChange("title", value);
    }

    private onImageChanged = async (image: ImageGallery | undefined) => {
        if (!image) await this.onTranslationMessageChange("imageLink", undefined);
        await this.onTranslationMessageChange("image", image);
    }

    private onBannerChanged = async (banner: ImageGallery | undefined) => {
        if (!banner) await this.onTranslationMessageChange("bannerLink", undefined);
        await this.onTranslationMessageChange("banner", banner);
    }

    private onSummaryChanged = async (value: string) => {
        await this.onTranslationMessageChange("message", value);
    }

    private onSubMessageChanged = async (value: string) => {
        await this.onTranslationMessageChange("subMessage", value);
    }

    private onAuthorChanged = async (value: string) => {
        await this.onTranslationMessageChange("author", value);
    }

    private onBtnTitleChanged = async (index: number, value: string) => {
        await this.onTranslationMessageChange("title", value, index);
    }

    private onBtnLinkChanged = async (index: number, value: string) => {
        await this.onTranslationMessageChange("link", value, index);
    }

    private onBtnActionTypeChanged = async (index: number, value: ButtonActionType) => {
        await this.onTranslationMessageChange("actionType", value, index);
        if (value === ButtonActionType.Link) {
            await this.onTranslationMessageChange("script", "", index);
            await this.onTranslationMessageChange("link", "", index);
        } else {
            await this.onTranslationMessageChange("link", "", index);
        }
    }

    private onBtnScriptChanged = async (index: number, value: IFilePickerFile | undefined) => {
        await this.onTranslationMessageChange("script", value, index);
    }

    private updateCard = () => {
        updateCard(this.card, this.state.draftMessage, this.context.topics.list, this.context.languages.list, this.state.currentTranslation);
    }
}

export default withRouter(NewMessage);